import React, { useState, useEffect, useCallback } from "react";
import {
  Globe,
  RefreshCw,
  Search,
  Trash2,
  Eye,
  AlertCircle,
  Play,
  Loader2,
  FileText,
  Calendar,
  Link as LinkIcon,
  Database,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Plus,
  Edit,
  Tag,
} from "lucide-react";
import websiteContentApi, { COFFEEBEAN_TARGET_URL } from "../services/websiteContentApi";

const WebsiteCrawlManager = () => {
  const [contents, setContents] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(20);
  const [toast, setToast] = useState(null);
  const [viewingContent, setViewingContent] = useState(null);
  const [showCrawlModal, setShowCrawlModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [healthCheck, setHealthCheck] = useState(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [manualEntryForm, setManualEntryForm] = useState({ url: "", title: "", text: "" });
  const [filter, setFilter] = useState("all"); // "all", "manual", "crawled"
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const fetchContents = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await websiteContentApi.getAll(page, pageSize, debouncedSearchQuery, filter);
      const payload = response?.data?.data || response?.data;

      setContents(payload?.contents || []);
      setTotal(payload?.total || 0);
      setTotalPages(payload?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching website content:", error);
      showToast("Failed to load website content", "error");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, debouncedSearchQuery, filter]);

  const fetchStats = useCallback(async () => {
    try {
      setIsStatsLoading(true);
      const response = await websiteContentApi.getStats();
      setStats(response?.data?.data || response?.data || null);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Debounce search query (wait 500ms after user stops typing)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        if (viewingContent) {
          setViewingContent(null);
        } else if (showManualEntryModal && !isSubmitting) {
          setShowManualEntryModal(false);
          setEditingContent(null);
          setManualEntryForm({ url: "", title: "", text: "" });
        } else if (showCrawlModal && !isSubmitting) {
          setShowCrawlModal(false);
          setHealthCheck(null);
        } else if (showDeleteAllModal && !isSubmitting) {
          setShowDeleteAllModal(false);
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [viewingContent, showManualEntryModal, showCrawlModal, showDeleteAllModal, isSubmitting]);

  const handleHealthCheck = async () => {
    try {
      setIsCheckingHealth(true);
      const response = await websiteContentApi.checkHealth();
      setHealthCheck(response.data);
      
      if (response.data.overall) {
        showToast("System is ready for crawling!", "success");
      } else {
        showToast("System has configuration issues. Check health status below.", "error");
      }
    } catch (error) {
      console.error("Error checking system health:", error);
      showToast("Failed to check system health", "error");
      setHealthCheck(null);
    } finally {
      setIsCheckingHealth(false);
    }
  };

  const handleCrawlSite = async () => {
    try {
      setIsSubmitting(true);
      const response = await websiteContentApi.crawlSite();
      
      // Background crawl started successfully
      setShowCrawlModal(false);
      setHealthCheck(null);
      showToast("Crawl started in background! This may take several minutes. Refresh to see new content.", "success");
      
      // Auto-refresh after 30 seconds to show initial results
      setTimeout(() => {
        fetchContents();
        fetchStats();
      }, 30000);
    } catch (error) {
      console.error("Error crawling Coffee Bean site:", error);
      const errorMsg =
        error.response?.data?.error || 
        error.response?.data?.message ||
        error.message ||
        "Failed to start crawl. Please try again.";
      showToast(errorMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCrawlAll = async () => {
    if (!confirm("This will crawl all configured URLs. This may take 5-10 minutes. Continue?")) {
      return;
    }

    try {
      setIsSubmitting(true);
      await websiteContentApi.crawlAll();
      showToast("Crawl started in background! This may take several minutes. Refresh to see new content.", "success");
      // Refresh after 30 seconds to see initial results
      setTimeout(() => {
        fetchContents();
        fetchStats();
      }, 30000);
    } catch (error) {
      console.error("Error triggering full crawl:", error);
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to start crawl. Please try again.";
      showToast(errorMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewContent = async (id) => {
    try {
      setIsLoadingContent(true);
      const response = await websiteContentApi.getById(id);
      setViewingContent(response?.data?.data || response?.data || null);
    } catch (error) {
      console.error("Error fetching content details:", error);
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to load content details. Please try again.";
      showToast(errorMsg, "error");
    } finally {
      setIsLoadingContent(false);
    }
  };

  const handleDelete = async (id) => {
    // Find the content to check if it's manual
    const content = contents.find(c => c.id === id);
    const isManual = content?.isManual;
    
    const confirmMessage = isManual
      ? "⚠️ This is a MANUALLY ADDED entry. Are you sure you want to delete it? This action cannot be undone."
      : "Are you sure you want to delete this content? This action cannot be undone.";
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingId(id);
      await websiteContentApi.deleteById(id);
      showToast("Content deleted successfully!", "success");

      // If deleting the last item on the current page and not on page 1, go to previous page
      if (contents.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchContents();
      }
      fetchStats();
    } catch (error) {
      console.error("Error deleting content:", error);
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to delete content. Please try again.";
      showToast(errorMsg, "error");
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenManualEntryModal = () => {
    setEditingContent(null);
    setManualEntryForm({ url: "", title: "", text: "" });
    setShowManualEntryModal(true);
  };

  const handleEditContent = async (content) => {
    try {
      setIsLoadingContent(true);
      const response = await websiteContentApi.getById(content.id);
      const fullContent = response?.data?.data || response?.data || null;
      
      if (fullContent) {
        setEditingContent(fullContent);
        setManualEntryForm({
          url: fullContent.url,
          title: fullContent.title || "",
          text: fullContent.text,
        });
        setShowManualEntryModal(true);
      }
    } catch (error) {
      console.error("Error loading content for edit:", error);
      showToast("Failed to load content for editing", "error");
    } finally {
      setIsLoadingContent(false);
    }
  };

  const handleSaveManualEntry = async () => {
    // Validate - only text is required now
    if (!manualEntryForm.text.trim()) {
      showToast("Text content is required", "error");
      return;
    }

    try {
      setIsSubmitting(true);
      
      if (editingContent) {
        // Update existing
        await websiteContentApi.update(editingContent.id, {
          url: manualEntryForm.url.trim() || undefined,
          title: manualEntryForm.title.trim() || undefined,
          text: manualEntryForm.text.trim(),
        });
        showToast("Content updated successfully!", "success");
      } else {
        // Create new
        await websiteContentApi.createManual({
          url: manualEntryForm.url.trim() || undefined,
          title: manualEntryForm.title.trim() || undefined,
          text: manualEntryForm.text.trim(),
        });
        showToast("Manual entry created successfully!", "success");
      }

      setShowManualEntryModal(false);
      setEditingContent(null);
      setManualEntryForm({ url: "", title: "", text: "" });
      fetchContents();
      fetchStats();
    } catch (error) {
      console.error("Error saving manual entry:", error);
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to save manual entry. Please try again.";
      showToast(errorMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAll = async () => {
    try {
      setIsSubmitting(true);
      const response = await websiteContentApi.deleteAll();
      const deletedCount = response?.data?.data?.deletedCount || response?.data?.deletedCount || 0;
      const manualPreserved = response?.data?.data?.manualEntriesPreserved || response?.data?.manualEntriesPreserved || 0;
      
      const message = manualPreserved > 0
        ? `Deleted ${deletedCount} crawled ${deletedCount === 1 ? "entry" : "entries"}. ${manualPreserved} manual ${manualPreserved === 1 ? "entry" : "entries"} preserved.`
        : `Successfully deleted ${deletedCount} content ${deletedCount === 1 ? "entry" : "entries"}`;
      
      showToast(message, "success");
      setShowDeleteAllModal(false);
      // Reset to page 1 after deleting all
      setPage(1);
      setSearchQuery("");
      fetchContents();
      fetchStats();
    } catch (error) {
      console.error("Error deleting all content:", error);
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to delete all content. Please try again.";
      showToast(errorMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Pages</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {isStatsLoading ? (
                  <Loader2 className="animate-spin text-gray-400" size={24} />
                ) : (
                  stats?.totalContents || 0
                )}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Globe className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Chunks</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {isStatsLoading ? (
                  <Loader2 className="animate-spin text-gray-400" size={24} />
                ) : (
                  stats?.totalChunks || 0
                )}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Database className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Chunks/Page</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {isStatsLoading ? (
                  <Loader2 className="animate-spin text-gray-400" size={24} />
                ) : stats?.totalContents > 0 ? (
                  (stats.totalChunks / stats.totalContents).toFixed(1)
                ) : (
                  "0"
                )}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <FileText className="text-purple-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Top Bar with Search and Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Semantic search (uses AI to find relevant content)..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-3 h-10 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchQuery !== debouncedSearchQuery && searchQuery.length > 0 && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 size={14} className="animate-spin text-blue-500" />
              </div>
            )}
          </div>

          {/* Filter Dropdown */}
          <select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(1);
            }}
            className="h-10 px-3 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
          >
            <option value="all">All Content</option>
            <option value="manual">Manual Only</option>
            <option value="crawled">Crawled Only</option>
          </select>

          <button
            onClick={fetchContents}
            className="h-10 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors inline-flex items-center gap-2 text-sm font-medium text-gray-700"
            disabled={isLoading}
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Refresh</span>
          </button>

          <button
            onClick={handleOpenManualEntryModal}
            className="h-10 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center gap-2 text-sm font-semibold"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Add</span>
          </button>

          <button
            onClick={() => setShowCrawlModal(true)}
            className="h-10 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2 text-sm font-semibold"
          >
            <Play size={16} />
            <span className="hidden sm:inline">Crawl CoffeeBean</span>
          </button>

          <button
            onClick={handleCrawlAll}
            disabled={isSubmitting}
            className="h-10 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors inline-flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
          >
            <Globe size={16} />
            <span className="hidden sm:inline">Crawl All</span>
          </button>

          <button
            onClick={() => setShowDeleteAllModal(true)}
            className="h-10 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors inline-flex items-center gap-2 text-sm font-semibold"
            title="Delete all crawled content (manual entries will be preserved)"
          >
            <Trash2 size={16} />
            <span className="hidden sm:inline">Delete All Crawled</span>
          </button>
        </div>
      </div>

      {/* Content Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={40} className="animate-spin text-blue-600" />
          </div>
        ) : contents.length === 0 ? (
          <div className="text-center py-12">
            <Globe size={64} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {filter === "manual" 
                ? "No manual entries found"
                : filter === "crawled"
                ? "No crawled content found"
                : "No content found"}
            </h3>
            <p className="text-gray-600 mb-6">
              {debouncedSearchQuery
                ? `Try adjusting your search query${filter !== "all" ? " or filter" : ""}`
                : filter === "manual"
                ? "Create your first manual entry"
                : filter === "crawled"
                ? "Start by crawling a URL or triggering a full crawl"
                : "Start by crawling a URL or adding a manual entry"}
            </p>
            {!debouncedSearchQuery && filter !== "manual" && (
              <button
                onClick={() => setShowCrawlModal(true)}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2 font-medium"
              >
                <Play size={20} />
                Crawl CoffeeBean.in
              </button>
            )}
            {!debouncedSearchQuery && filter !== "crawled" && (
              <button
                onClick={handleOpenManualEntryModal}
                className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center gap-2 font-medium ml-3"
              >
                <Plus size={20} />
                Add
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Pagination Top */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between text-sm">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{" "}
                <span className="font-medium">{Math.min(page * pageSize, total)}</span> of{" "}
                <span className="font-medium">{total}</span> results
                {filter !== "all" && (
                  <span className="ml-2 text-xs text-gray-500">
                    ({filter === "manual" ? "manual only" : "crawled only"})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm font-medium text-gray-700">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      URL
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Chunks
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {contents.map((content) => (
                    <tr key={content.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        {content.url ? (
                          <div className="flex items-start gap-2">
                            <LinkIcon size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
                            <a
                              href={content.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline text-sm max-w-xs line-clamp-2 break-all"
                              title={content.url}
                            >
                              {content.url}
                            </a>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic text-sm">No URL</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <div
                            className="text-sm text-gray-900 max-w-xs line-clamp-2 break-words"
                            title={content.title || "No title"}
                          >
                            {content.title || <span className="text-gray-400 italic">No title</span>}
                          </div>
                          {content.isManual && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700 w-fit">
                              <Tag size={12} />
                              Manual Entry
                            </span>
                          )}
                          {content.manuallyEdited && !content.isManual && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 w-fit">
                              <Edit size={12} />
                              Edited
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {content.chunkCount}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm text-gray-600">
                          {formatFileSize(content.textLength)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <Calendar size={12} className="text-gray-400" />
                          {formatDate(content.updatedAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleViewContent(content.id)}
                            disabled={isLoadingContent}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="View Details"
                          >
                            {isLoadingContent ? (
                              <Loader2 size={18} className="animate-spin" />
                            ) : (
                              <Eye size={18} />
                            )}
                          </button>
                          {content.isManual && (
                            <button
                              onClick={() => handleEditContent(content)}
                              disabled={isLoadingContent}
                              className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Edit"
                            >
                              <Edit size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(content.id)}
                            disabled={deletingId === content.id}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={content.isManual ? "Delete Manual Entry" : "Delete"}
                          >
                            {deletingId === content.id ? (
                              <Loader2 size={18} className="animate-spin" />
                            ) : (
                              <Trash2 size={18} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Bottom */}
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{" "}
                <span className="font-medium">{Math.min(page * pageSize, total)}</span> of{" "}
                <span className="font-medium">{total}</span> results
                {filter !== "all" && (
                  <span className="ml-2 text-xs text-gray-500">
                    ({filter === "manual" ? "manual only" : "crawled only"})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm font-medium text-gray-700">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Crawl CoffeeBean Modal */}
      {showCrawlModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isSubmitting) {
              setShowCrawlModal(false);
              setHealthCheck(null);
            }
          }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Play className="text-blue-600" size={20} />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Crawl CoffeeBean.in
              </h2>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              We crawl the official Coffee Bean website and automatically follow every internal
              page, including nested `/pages` entries. Headers, footers, and navigation chrome are
              stripped before ingestion so that only meaningful content reaches the knowledge base.
              <strong className="block mt-2">The crawl runs in the background and may take 5-10 minutes to complete.</strong>
            </p>

                {/* Health Check Status */}
                {healthCheck && (
                  <div className={`mb-4 p-4 rounded-lg border ${
                    healthCheck.overall 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {healthCheck.overall ? (
                        <CheckCircle size={18} className="text-green-600" />
                      ) : (
                        <XCircle size={18} className="text-red-600" />
                      )}
                      <span className={`text-sm font-semibold ${
                        healthCheck.overall ? 'text-green-700' : 'text-red-700'
                      }`}>
                        System Status: {healthCheck.overall ? 'Ready' : 'Not Ready'}
                      </span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        {healthCheck.openai.configured && healthCheck.openai.working ? (
                          <CheckCircle size={14} className="text-green-600" />
                        ) : (
                          <XCircle size={14} className="text-red-600" />
                        )}
                        <span className="text-gray-700">
                          OpenAI API: {healthCheck.openai.configured 
                            ? (healthCheck.openai.working ? 'Working' : `Error: ${healthCheck.openai.error}`)
                            : 'Not Configured'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {healthCheck.database.connected ? (
                          <CheckCircle size={14} className="text-green-600" />
                        ) : (
                          <XCircle size={14} className="text-red-600" />
                        )}
                        <span className="text-gray-700">
                          Database: {healthCheck.database.connected ? 'Connected' : 'Disconnected'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {healthCheck.database.vectorExtension ? (
                          <CheckCircle size={14} className="text-green-600" />
                        ) : (
                          <XCircle size={14} className="text-red-600" />
                        )}
                        <span className="text-gray-700">
                          Vector Extension: {healthCheck.database.vectorExtension ? 'Installed' : 'Missing'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4 mb-6">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      Target domain
                    </p>
                    <a
                      href={COFFEEBEAN_TARGET_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 break-all text-sm font-semibold"
                    >
                      {COFFEEBEAN_TARGET_URL}
                    </a>
                  </div>

                  <ul className="list-disc pl-5 text-sm text-gray-600 space-y-2">
                    <li>Recursively crawls every internal link (including `/pages/...` content).</li>
                    <li>Header, footer, nav, script, and style sections are removed automatically.</li>
                    <li>
                      Runs with safe depth (6 levels) and page (300) limits to cover the full site.
                    </li>
                  </ul>
                </div>

                <div className="flex items-center gap-3 justify-between">
                  <button
                    onClick={handleHealthCheck}
                    disabled={isCheckingHealth || isSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {isCheckingHealth ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <AlertCircle size={16} />
                        Check System
                      </>
                    )}
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        setShowCrawlModal(false);
                        setHealthCheck(null);
                      }}
                      disabled={isSubmitting}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCrawlSite}
                      disabled={isSubmitting || (healthCheck && !healthCheck.overall)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <Play size={16} />
                          Start Crawl
                        </>
                      )}
                    </button>
                  </div>
                </div>
          </div>
        </div>
      )}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isSubmitting) {
              setShowDeleteAllModal(false);
            }
          }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Delete All Crawled Content</h2>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete all <strong>crawled</strong> website content? This will remove all
              auto-crawled pages and their chunks from the knowledge base.
            </p>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-purple-900 font-medium flex items-center gap-2">
                <Tag size={16} />
                Manual entries will be preserved
              </p>
              <p className="text-xs text-purple-700 mt-1">
                Only auto-crawled content will be deleted. Your manually added entries are protected.
              </p>
            </div>
            <p className="text-sm text-red-600 font-medium">
              ⚠️ This action cannot be undone.
            </p>

            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setShowDeleteAllModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAll}
                disabled={isSubmitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete All
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Content Modal */}
      {viewingContent && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setViewingContent(null);
            }
          }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="text-blue-600" size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Content Details</h2>
                  <p className="text-sm text-gray-600">{viewingContent.chunks.length} chunks</p>
                </div>
              </div>
              <button
                onClick={() => setViewingContent(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <XCircle size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {/* URL */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">URL</label>
                  {viewingContent.url ? (
                    <a
                      href={viewingContent.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline text-sm break-all"
                    >
                      {viewingContent.url}
                    </a>
                  ) : (
                    <span className="text-gray-400 italic text-sm">No URL</span>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
                  <p className="text-sm text-gray-900">
                    {viewingContent.title || <span className="text-gray-400 italic">No title</span>}
                  </p>
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Created At
                    </label>
                    <p className="text-sm text-gray-900">{formatDate(viewingContent.createdAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Updated At
                    </label>
                    <p className="text-sm text-gray-900">{formatDate(viewingContent.updatedAt)}</p>
                  </div>
                </div>

                {/* Full Text */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Text ({formatFileSize(viewingContent.text.length)})
                  </label>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                      {viewingContent.text}
                    </p>
                  </div>
                </div>

                {/* Chunks */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Chunks ({viewingContent.chunks.length})
                  </label>
                  <div className="space-y-3">
                    {viewingContent.chunks.map((chunk, index) => (
                      <div
                        key={chunk.id}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-500">
                            Chunk {index + 1}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatFileSize(chunk.chunkText.length)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 font-mono whitespace-pre-wrap">
                          {chunk.chunkText}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setViewingContent(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-semibold hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Entry Modal */}
      {showManualEntryModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isSubmitting) {
              setShowManualEntryModal(false);
              setEditingContent(null);
              setManualEntryForm({ url: "", title: "", text: "" });
            }
          }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  {editingContent ? <Edit className="text-purple-600" size={20} /> : <Plus className="text-purple-600" size={20} />}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {editingContent ? "Edit Content" : "Add Manual Entry"}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {editingContent ? "Update the content details below" : "Manually add website content to the knowledge base"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowManualEntryModal(false);
                  setEditingContent(null);
                  setManualEntryForm({ url: "", title: "", text: "" });
                }}
                disabled={isSubmitting}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                <XCircle size={24} />
              </button>
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {/* URL */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={manualEntryForm.url}
                    onChange={(e) => setManualEntryForm({ ...manualEntryForm, url: e.target.value })}
                    placeholder="https://example.com/page (optional)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    disabled={isSubmitting}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional - Leave blank for content without a specific URL (e.g., FAQs, documentation)
                  </p>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Title (Optional)
                  </label>
                  <input
                    type="text"
                    value={manualEntryForm.title}
                    onChange={(e) => setManualEntryForm({ ...manualEntryForm, title: e.target.value })}
                    placeholder="Title"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Text Content */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={manualEntryForm.text}
                    onChange={(e) => setManualEntryForm({ ...manualEntryForm, text: e.target.value })}
                    placeholder="Enter the main text content here. This will be chunked and embedded for semantic search."
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm resize-y"
                    disabled={isSubmitting}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The content will be automatically chunked and embedded with OpenAI for semantic search
                  </p>
                </div>

                {editingContent && editingContent.isManual && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <Tag size={16} className="text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-purple-900">Manual Entry</p>
                        <p className="text-xs text-purple-700 mt-1">
                          This is a manually added entry and will not be deleted during automatic crawls.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowManualEntryModal(false);
                  setEditingContent(null);
                  setManualEntryForm({ url: "", title: "", text: "" });
                }}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveManualEntry}
                disabled={isSubmitting || !manualEntryForm.text.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {editingContent ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <>
                    {editingContent ? <Edit size={16} /> : <Plus size={16} />}
                    {editingContent ? "Update Content" : "Create Entry"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div
            className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 ${
              toast.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
            }`}
          >
            {toast.type === "success" ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteCrawlManager;
